import { action, computed, makeObservable, observable } from 'mobx';
import {
  GetWorkspaceInfoResponse,
  WorkspaceSettings,
} from '../apiClients/workspace/types';
import { workspaceApi } from '../apiClients/workspace/workspaceApi';
import i18n from '../i18n';
import { notificationStore } from './NotificationStore';

export class WorkspaceStore {
  @observable workspace: GetWorkspaceInfoResponse | null = null;
  @observable isLoading = false;
  @observable error: Error | null = null;

  constructor() {
    makeObservable(this);
  }

  // ===============================
  // COMPUTED PROPERTIES
  // ===============================

  @computed get isError(): boolean {
    return this.error !== null;
  }

  @computed get hasData(): boolean {
    return this.workspace !== null;
  }

  @computed get workspaceName(): string | null {
    return this.workspace?.name || null;
  }

  @computed get workspaceSlug(): string | null {
    return this.workspace?.slug || null;
  }

  @computed get workspaceIconUrl(): string | null {
    return this.workspace?.iconUrl
      ? `${process.env.REACT_APP_API_BASE_URL}/api/file?path=${this.workspace.iconUrl}`
      : null;
  }

  @computed get settings(): WorkspaceSettings | null {
    return this.workspace?.settings || null;
  }

  // Convenience getters
  @computed get confirmationStartTimeMinutes(): number | null {
    return this.settings?.confirmationStartTimeMinutes ?? null;
  }

  @computed get maxLevelDifferenceForReplacement(): number | null {
    return this.settings?.maxLevelDifferenceForReplacement ?? null;
  }

  @computed get freeCancellationDeadlineMinutes(): number | null {
    return this.settings?.freeCancellationDeadlineMinutes ?? null;
  }

  @computed get cancellationPenaltyPercent(): number | null {
    // return this.settings?.cancellationPenaltyPercent ?? null;
    return 100;
  }

  // ===============================
  // ACTIONS
  // ===============================

  @action async loadWorkspaceInfo(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const workspace = await workspaceApi.getWorkspaceInfo();
      this.workspace = workspace;
    } catch (error) {
      this.error = error as Error;
      notificationStore.showError(i18n.t('errors.failedToLoadWorkspaceData'));
    } finally {
      this.isLoading = false;
    }
  }

  @action clear(): void {
    this.workspace = null;
    this.isLoading = false;
    this.error = null;
  }

  // ===============================
  // LIFECYCLE METHODS
  // ===============================

  async initialize(): Promise<void> {
    await this.loadWorkspaceInfo();
  }

  destroy(): void {
    this.clear();
  }
}

// Export singleton instance
export const workspaceStore = new WorkspaceStore();
